/**
 * Landing motion contract — the SINGLE numeric source for the round-3 marketing
 * landing (Judge F verdict §2.3, as amended by the R3 debate rulings). Every
 * vanilla `motion` animate() / raw-WAAPI call site on the landing imports these
 * tokens; CSS-only consumers read the matching custom properties declared under
 * `.landing-motion-scope` in globals.css. No landing component copies a literal
 * duration at an animate()/transition call site — `scripts/check-landing-
 * choreography.mjs` bans inline duration literals outside this file (the sole
 * exception is each scene's ONE frozen `*_TIMING` table, per rulings Harm. f).
 *
 * `settle` (700ms) was intentionally REMOVED here: its only consumer was the
 * NarrativeHold sticky panel, which R3 debate RULING 1 struck entirely. Do not
 * reintroduce it — there is no held panel on the landing.
 *
 * Everything is frozen (`as const`) so a stray mutation cannot drift one scene's
 * timing off-contract at runtime.
 */

/** Durations in milliseconds. Divide by 1000 (`msToSec`) for Motion's second API. */
export const LANDING_MOTION_MS = {
  /** Pointer press acknowledgement only. */
  press: 90,
  /** Hover/focus colour or opacity; never chapter entrances. */
  fast: 160,
  /** Small interactive state changes. */
  ui: 220,
  /** Universal below-fold entrance, 12px maximum travel. */
  enter: 420,
  /** Connector draw and one discrete demo action. */
  demoStep: 520,
  /** Opacity-only reduced-motion text reveal. */
  reduced: 320,
} as const;

export type LandingMotionKey = keyof typeof LANDING_MOTION_MS;

/**
 * Easing as cubic-bezier control-point tuples — for Motion's `ease` option
 * (`animate(el, kf, { ease: LANDING_EASE.enter })`).
 */
export const LANDING_EASE = {
  /** Hover/focus and reversible UI. */
  ui: [0.2, 0.8, 0.2, 1],
  /** All entrances, connector masks, sticky settles. */
  enter: [0.22, 1, 0.36, 1],
  /** Finite exits only; no exit on scroll-away. */
  exit: [0.4, 0, 1, 1],
  /** Progress-like motion only: chart sweeps, opacity crossfades, cursor
   * position — never an entrance/exit/hover curve. Bezier (0,0,1,1) is
   * mathematically identical to constant-rate linear timing. */
  linear: [0, 0, 1, 1],
  /** R4-P2 primitive library micro/press interactions, ~.3-.4s (build brief
   * §1.6, lusion-measured). Distinct from `ui` — reserved for the new
   * primitive layer's hover/press choreography, not legacy scene hovers. */
  micro: [0.4, 0, 0.1, 1],
  /** R4-P2 primitive library reveal / CTA-internal motion, ~.4-.6s (build
   * brief §1.6, lusion-measured). Distinct from `enter` — reserved for the
   * new primitive layer, not legacy section entrances. */
  reveal: [0.35, 0, 0, 1],
} as const satisfies Record<string, readonly [number, number, number, number]>;

/**
 * The same easing curves as `cubic-bezier()` strings — for raw WAAPI
 * (`element.animate(kf, { easing: LANDING_EASE_CSS.enter })`), which takes a CSS
 * timing-function string, not a tuple. Single source with LANDING_EASE above.
 */
export const LANDING_EASE_CSS = {
  ui: 'cubic-bezier(0.2, 0.8, 0.2, 1)',
  enter: 'cubic-bezier(0.22, 1, 0.36, 1)',
  exit: 'cubic-bezier(0.4, 0, 1, 1)',
  /** Same "progress-like motion" scope as LANDING_EASE.linear above — for raw
   * WAAPI call sites that need a CSS timing-function string. */
  linear: 'linear',
  /** Same scope as LANDING_EASE.micro above — CSS timing-function string. */
  micro: 'cubic-bezier(0.4, 0, 0.1, 1)',
  /** Same scope as LANDING_EASE.reveal above — CSS timing-function string. */
  reveal: 'cubic-bezier(0.35, 0, 0, 1)',
} as const;

export type LandingEaseKey = keyof typeof LANDING_EASE;

/** Stagger step between siblings. Maximum three siblings: 0 / 60 / 120ms. */
export const LANDING_STAGGER_MS = 60;

/**
 * in-view trigger geometry. `entrance` = once-and-hold section content;
 * `scene` = product/social/bot demos (300ms dwell, cancelled on early exit);
 * `connector` = desktop connector draw (once); `hero` = hero's sole gated loop.
 */
export const LANDING_INVIEW = {
  entrance: { amount: 0.2, margin: '0px 0px -12% 0px' },
  scene: { amount: 0.6, dwellMs: 300 },
  connector: { amount: 0.6, margin: '0px 0px -8% 0px' },
  hero: { amount: 0.2, delayMs: 650 },
} as const;

/** Runtime budget probes (Judge F Dec 9) — enforced live via getAnimations(). */
export const MAX_DESKTOP_RUNNING_EFFECTS = 6;
export const MAX_MOBILE_AUTOMATIC_EFFECTS = 1;

/** Milliseconds → seconds, for Motion's second-based `duration`/`delay` options. */
export const msToSec = (ms: number): number => ms / 1000;
